import React, { useState, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, ReferenceDot, AreaChart, Area
} from 'recharts';
import { Dropdown, IDropdownOption, IconButton, TooltipHost } from '@fluentui/react';
import { ArrowDownload20Regular, Image20Regular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

interface ChartData {
  type: string;
  data?: Array<{ label: string; value: number } | { x: string; y: number }>;
  series?: Record<string, Array<any>>;
  rows?: string[];
  columns?: string[];
  values?: number[][];
  title?: string;
  xLabel?: string;
  yLabel?: string;
  format?: string;
  xKey?: string;
  yKey?: string;
  seriesKey?: string;
  labelKey?: string;
  valueKey?: string;
}

interface ChartRendererProps {
  chartData: ChartData | string | any;  // Allow legacy formats
}

// Chart type options for dropdown
type SwitchableChartType = 'bar' | 'horizontalBar' | 'pie' | 'donut' | 'line' | 'area';

const CHART_TYPE_OPTIONS: IDropdownOption[] = [
  { key: 'bar', text: 'Bar Chart' },
  { key: 'horizontalBar', text: 'Horizontal Bar' },
  { key: 'pie', text: 'Pie Chart' },
  { key: 'donut', text: 'Donut Chart' },
  { key: 'line', text: 'Line Chart' },
  { key: 'area', text: 'Area Chart' },
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Color palette for charts
const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#0088fe', '#00c49f', '#ffbb28', '#ff8042', '#8dd1e1'];

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartTypeOverride, setChartTypeOverride] = useState<SwitchableChartType | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  console.log('🎯 Modern ChartRenderer received:', chartData);
  
  // Export chart to image
  const handleExportImage = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const title = chartData?.title || 'chart';
          saveAs(blob, `${title.replace(/[^a-zA-Z0-9]/g, '_')}.png`);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error exporting chart:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  if (!chartData) {
    return <div>No chart data available</div>;
  }
  
  // Handle base64 image strings (legacy format)
  if (typeof chartData === 'string') {
    return <img src={`data:image/png;base64,${chartData}`} alt="Generated Chart" style={{ maxWidth: '100%', height: 'auto' }} />;
  }
  
  if (typeof chartData !== 'object') {
    console.log('🎯 ChartRenderer: Invalid chart data type:', typeof chartData);
    return <div>Invalid chart data</div>;
  }

  // Determine effective chart type (allow switching between compatible types)
  const originalType = chartData.type;
  const isSwitchableType = ['bar', 'horizontalBar', 'pie', 'donut', 'line', 'area'].includes(originalType);
  const effectiveType = chartTypeOverride || originalType;

  const commonStyle = { width: '100%', height: '350px' };
  const titleStyle = { textAlign: 'center' as const, marginBottom: '10px' };
  
  // Transform data for Recharts (handle both label/value and x/y formats)
  const transformData = () => {
    if (!chartData.data) return [];
    return chartData.data.map((item: any) => {
      if ('label' in item && 'value' in item) {
        return { name: item.label, value: item.value };
      } else if ('x' in item && 'y' in item) {
        return { name: item.x, value: item.y };
      }
      return { name: 'Unknown', value: 0 };
    });
  };

  // Render based on effective chart type
  const renderChart = () => {
    const transformedData = transformData();
    
    // Bar Chart
    if (effectiveType === 'bar' && chartData.data) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={transformedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" label={{ value: chartData.xLabel || '', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: chartData.yLabel || '', angle: -90, position: 'insideLeft' }} 
                   tickFormatter={chartData.format === 'currency' ? formatCurrency : undefined} />
            <Tooltip formatter={chartData.format === 'currency' ? (value: number) => [formatCurrency(value), chartData.yLabel || 'Value'] : undefined} />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    
    // Horizontal Bar Chart
    if (effectiveType === 'horizontalBar' && chartData.data) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={transformedData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={chartData.format === 'currency' ? formatCurrency : undefined} />
            <YAxis type="category" dataKey="name" width={70} />
            <Tooltip formatter={chartData.format === 'currency' ? (value: number) => [formatCurrency(value), 'Value'] : undefined} />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    
    // Line Chart
    if (effectiveType === 'line' && chartData.data) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={transformedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={chartData.format === 'currency' ? formatCurrency : undefined} />
            <Tooltip formatter={chartData.format === 'currency' ? (value: number) => [formatCurrency(value), 'Value'] : undefined} />
            <Legend />
            <Line type="monotone" dataKey="value" stroke="#8884d8" />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    
    // Area Chart
    if (effectiveType === 'area' && chartData.data) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={transformedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={chartData.format === 'currency' ? formatCurrency : undefined} />
            <Tooltip formatter={chartData.format === 'currency' ? (value: number) => [formatCurrency(value), 'Value'] : undefined} />
            <Legend />
            <Area type="monotone" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    
    // Pie Chart
    if ((effectiveType === 'pie' || effectiveType === 'donut') && chartData.data) {
      const labelKey = chartData.labelKey || 'label';
      const valueKey = chartData.valueKey || 'value';
      const pieData = chartData.data.map((item: any, index: number) => ({
        name: labelKey in item ? item[labelKey] : ('label' in item ? item.label : `Item ${index + 1}`),
        value: valueKey in item ? item[valueKey] : ('value' in item ? item.value : ('y' in item ? item.y : 0))
      }));
      const innerRadius = effectiveType === 'donut' ? 40 : 0;
      
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              innerRadius={innerRadius}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={chartData.format === 'currency' ? (value: number) => [formatCurrency(value), 'Value'] : undefined} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  // For switchable chart types with data, show the enhanced interface
  if (isSwitchableType && chartData.data) {
    return (
      <div style={{ width: '100%', marginTop: '20px' }} data-visual="chart">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '10px',
          padding: '0 10px'
        }}>
          <h3 style={{ margin: 0 }}>{chartData.title || 'Chart'}</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Dropdown
              selectedKey={effectiveType}
              options={CHART_TYPE_OPTIONS}
              onChange={(_, option) => option && setChartTypeOverride(option.key as SwitchableChartType)}
              styles={{
                root: { minWidth: 120 },
                dropdown: { fontSize: 12 },
                title: { fontSize: 12, height: 28, lineHeight: '26px' },
                caretDownWrapper: { height: 28, lineHeight: '26px' }
              }}
            />
            <TooltipHost content="Export as Image">
              <IconButton
                iconProps={{ iconName: 'Photo2' }}
                onClick={handleExportImage}
                disabled={isExporting}
                styles={{ root: { height: 28, width: 28 } }}
              />
            </TooltipHost>
          </div>
        </div>
        <div ref={chartRef} style={commonStyle}>
          {renderChart()}
        </div>
      </div>
    );
  }

  // Original fallback for non-switchable types (heatmap, multi-series line, etc.)
  
  // Multi-series Line Chart (special case with series data)
  if (chartData.type === 'line' && chartData.series) {
    const seriesKeys = Object.keys(chartData.series);
    const allXValues = Array.from(new Set(
      Object.values(chartData.series).flat().map((point: any) => point[chartData.xKey || 'x'])
    )).sort();

    const transformedData = allXValues.map(xVal => {
      const dataPoint: any = { [chartData.xKey || 'x']: xVal };
      seriesKeys.forEach(seriesKey => {
        const point = chartData.series![seriesKey].find((p: any) => p[chartData.xKey || 'x'] === xVal);
        dataPoint[seriesKey] = point ? point[chartData.yKey || 'y'] : null;
      });
      return dataPoint;
    });

    return (
      <div style={{ width: '100%', height: '400px', marginTop: '20px' }}>
        <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>{chartData.title || 'Line Chart'}</h3>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={transformedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={chartData.xKey || 'x'} />
            <YAxis />
            <Tooltip />
            <Legend />
            {seriesKeys.map((seriesKey, index) => (
              <Line 
                key={seriesKey}
                type="monotone" 
                dataKey={seriesKey} 
                stroke={CHART_COLORS[index % CHART_COLORS.length]} 
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Heatmap (simplified representation using a grid)
  if (chartData.type === 'heatmap' && chartData.values && chartData.rows && chartData.columns) {
    const maxValue = Math.max(...chartData.values.flat());
    const minValue = Math.min(...chartData.values.flat());
    
    return (
      <div style={{ width: '100%', height: 'auto', minHeight: '300px', marginTop: '20px' }}>
        <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>{chartData.title || 'Heatmap'}</h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: `100px repeat(${chartData.columns.length}, 1fr)`,
          gap: '2px',
          padding: '20px'
        }}>
          {/* Header row */}
          <div></div>
          {chartData.columns.map((col: string, index: number) => (
            <div key={index} style={{ 
              padding: '8px', 
              textAlign: 'center', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {col}
            </div>
          ))}
          
          {/* Data rows */}
          {chartData.rows.map((row: string, rowIndex: number) => (
            <React.Fragment key={rowIndex}>
              <div style={{ 
                padding: '8px', 
                textAlign: 'right', 
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {row}
              </div>
              {chartData.values![rowIndex].map((value: number, colIndex: number) => {
                const intensity = (value - minValue) / (maxValue - minValue);
                const backgroundColor = `rgba(136, 132, 216, ${intensity})`;
                return (
                  <div
                    key={colIndex}
                    style={{
                      padding: '8px',
                      backgroundColor,
                      textAlign: 'center',
                      fontSize: '11px',
                      border: '1px solid #ddd'
                    }}
                    title={`${row} × ${chartData.columns![colIndex]}: ${value.toFixed(2)}`}
                  >
                    {value.toFixed(1)}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return <div>Chart type "{chartData.type}" not supported</div>;
};

export default ChartRenderer;